/**
 * Shared Report Page
 *
 * Public read-only view for shared LLM scan reports.
 * Does not require authentication.
 */

import { useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertCircle, Lock, ShieldAlert, Shield, ArrowRight, X, Table as TableIcon } from "lucide-react";
import { RiskScorecard } from "@/components/llm/RiskScorecard";
import { FindingsWithRemediation } from "@/components/llm/FindingsWithRemediation";
import { VulnerabilitiesTable } from "@/components/llm/VulnerabilitiesTable";
import { VulnerabilityDetailModal } from "@/components/llm/VulnerabilityDetailModal";
import { useAccessShare, useSharedScanResults, useValidateShareToken } from "@/hooks/useShareScan";
import { classifyIssues, calculateVerdict } from "@/utils/issueClassification";
import type { LLMScanResult } from "@/types/llm";

/**
 * Promotional banner component for shared reports.
 * Encourages visitors to sign up and scan their own LLM applications.
 */
function PromoBanner({
  onDismiss,
  onStartScan,
}: {
  onDismiss: () => void;
  onStartScan: () => void;
}) {
  return (
    <div className="sticky top-0 z-50 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white shadow-lg">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm">
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-medium truncate">
              <span className="hidden sm:inline">Secure your AI. </span>
              Scan your LLM for vulnerabilities in minutes.
            </p>
            <p className="text-xs text-white/80 hidden md:block">
              Free security assessment • No credit card required • Results in under 5 minutes
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="secondary"
            size="sm"
            className="bg-white text-emerald-700 hover:bg-white/90 font-semibold shadow-md"
            onClick={onStartScan}
          >
            <span className="hidden sm:inline">Start Free Scan</span>
            <span className="sm:hidden">Try Free</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-md hover:bg-white/20 transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * MSSP partner banner — shown when the visitor lands with ?utm_campaign=mssp_q2.
 * Navy background with a green CTA pill, linking to the partner program page.
 */
function MsspBanner({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div
      className="sticky top-0 z-50 text-white shadow-lg"
      style={{ backgroundColor: "#0B1929" }}
    >
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-lg bg-white/10">
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-medium truncate">
              <span className="hidden sm:inline font-semibold">For MSSPs · </span>
              Resell AI security under your brand
            </p>
            <p className="text-xs text-white/70 hidden md:block">
              White-label scans, multi-tenant dashboards, partner margins
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href="https://vortal.so/for-mssps"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center font-semibold text-sm px-3 py-1.5 rounded-md shadow-md hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "#00D26A", color: "#0B1929" }}
          >
            <span className="hidden sm:inline">See the partner program</span>
            <span className="sm:hidden">Partner program</span>
            <ArrowRight className="h-4 w-4 ml-1" />
          </a>
          <button
            onClick={onDismiss}
            className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

const SUMMARY_ITEMS_PER_PAGE = 10000;

export function SharedReportPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMssp = searchParams.get("utm_campaign") === "mssp_q2";
  const [password, setPassword] = useState("");
  const [passwordAttempted, setPasswordAttempted] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [selectedResult, setSelectedResult] = useState<LLMScanResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const tableItemsPerPage = 10;
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    return sessionStorage.getItem("promo-banner-dismissed") === "true";
  });

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    sessionStorage.setItem("promo-banner-dismissed", "true");
  };

  // Validate token first
  const {
    data: tokenMetadata,
    isLoading: tokenLoading,
    error: tokenError,
  } = useValidateShareToken(token || "");

  const canClone = tokenMetadata && !tokenMetadata.requires_password;

  const handleCloneScan = () => {
    if (token && canClone) {
      navigate(`/compliance?cloneFrom=${token}`);
    } else {
      navigate("/compliance");
    }
  };

  const handleStartScan = () => {
    // Navigate to compliance page (general CTA, not cloning)
    navigate("/compliance");
  };

  // Access share - backend accepts null for non-password-protected shares
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useAccessShare(token || "", passwordAttempted ? password : null);

  // Get full, unfiltered results for summary widgets (risk scorecard, findings, etc.).
  // These sections should reflect the full scan, not just the current table page.
  const {
    data: fullResultsData,
    isLoading: fullResultsLoading,
    error: fullResultsError,
  } = useSharedScanResults(
    token || "",
    passwordAttempted ? password : null,
    1,
    SUMMARY_ITEMS_PER_PAGE
  );

  // Calculate verdict and issues - MUST be called before any conditional returns
  // Use empty arrays as fallbacks to ensure hooks are always called
  const allResults = fullResultsData?.data ?? [];
  const classifiedIssues = useMemo(() => classifyIssues(allResults), [allResults]);
  const verdict = useMemo(() => calculateVerdict(allResults), [allResults]);
  const blockingIssues = useMemo(
    () => classifiedIssues.filter((r) => r.category === "blocking" && r.status === "FAIL"),
    [classifiedIssues]
  );
  const advisoryIssues = useMemo(
    () => classifiedIssues.filter((r) => r.category === "advisory" && r.status === "FAIL"),
    [classifiedIssues]
  );

  if (!token) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <div className="text-sm sm:text-base">Invalid share token</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (tokenLoading) {
    return (
      <div className="container mx-auto p-4 sm:p-6 space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Token validation errors
  if (tokenError || !tokenMetadata) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <div>
                <div className="font-medium text-sm sm:text-base">
                  Invalid or expired share link
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {tokenError instanceof Error
                    ? tokenError.message
                    : "The share link is invalid or has expired."}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired or revoked
  if (tokenMetadata.expired || tokenMetadata.revoked) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-5 w-5" />
              <div>
                <div className="font-medium text-sm sm:text-base">
                  {tokenMetadata.expired ? "Share link has expired" : "Share link has been revoked"}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground mt-1">
                  {tokenMetadata.expired
                    ? "This share link is no longer valid. Please contact the report owner for a new link."
                    : "This share link has been revoked by the owner."}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password prompt - show form until summary is successfully loaded
  if (tokenMetadata.requires_password && !summary) {
    const handlePasswordSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (password.length >= 4) {
        setPasswordAttempted(true);
      }
    };

    return (
      <div className="container mx-auto p-4 sm:p-6 flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Password Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Enter password to view this report</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (summaryError) {
                      setPasswordAttempted(false);
                    }
                  }}
                  minLength={4}
                  required
                  autoFocus
                  autoComplete="current-password"
                  disabled={summaryLoading}
                />
              </div>
              {summaryLoading && (
                <div className="text-sm text-muted-foreground">Validating password...</div>
              )}
              {summaryError && passwordAttempted && (
                <div className="text-sm text-red-600">
                  {summaryError instanceof Error ? summaryError.message : "Incorrect password"}
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={password.length < 4 || summaryLoading}
              >
                {summaryLoading ? "Validating..." : "View Report"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading summary
  if (summaryLoading || !summary) {
    return (
      <div className="container mx-auto p-4 sm:p-6 space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const isFullResultsReady = !!fullResultsData;
  const summaryPartialWarning =
    !!fullResultsData &&
    fullResultsData.total_count > SUMMARY_ITEMS_PER_PAGE &&
    fullResultsData.data.length === SUMMARY_ITEMS_PER_PAGE;

  return (
    <div className="min-h-screen">
      {!bannerDismissed && (
        isMssp ? (
          <MsspBanner onDismiss={handleDismissBanner} />
        ) : (
          <PromoBanner onDismiss={handleDismissBanner} onStartScan={handleStartScan} />
        )
      )}

      <div className="container mx-auto p-4 sm:p-6 space-y-6">
        <div className="border-b pb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">AI Security Review</h1>
              <p className="text-sm text-muted-foreground mt-1">Read-only view of scan results</p>
            </div>
            <div className="hidden sm:block">
              {canClone ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloneScan}
                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Generate a similar report
                </Button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Generate a similar report
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {tokenMetadata?.requires_password
                        ? "Cannot clone password-protected reports"
                        : "Cloning not available"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>

        {/* Risk Scorecard */}
        <RiskScorecard
          summary={summary}
          results={allResults}
          isLoading={summaryLoading || fullResultsLoading || !isFullResultsReady}
          isPartialData={summaryPartialWarning}
          verdict={verdict}
        />

        {/* Findings with Inline Remediation */}
        <FindingsWithRemediation
          results={allResults}
          isLoading={fullResultsLoading || !isFullResultsReady}
          isPartialData={summaryPartialWarning}
          blockingIssues={blockingIssues}
          advisoryIssues={advisoryIssues}
        />

        {/* Audit Trail - Full test results (gated by share token's include_audit_trail flag) */}
        {(tokenMetadata?.include_audit_trail ?? true) && (
          <>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <TableIcon className="h-4 w-4" />
                  Audit Trail ({fullResultsData?.total_count ?? allResults.length})
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Complete test results for audit and verification
                </p>
              </CardHeader>
              <CardContent>
                <VulnerabilitiesTable
                  results={allResults}
                  isLoading={fullResultsLoading || !isFullResultsReady}
                  onResultClick={(result) => {
                    setSelectedResult(result);
                    setIsModalOpen(true);
                  }}
                  page={tablePage}
                  itemsPerPage={tableItemsPerPage}
                  onPageChange={setTablePage}
                />
              </CardContent>
            </Card>

            <VulnerabilityDetailModal
              result={selectedResult}
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
            />
          </>
        )}

        {!fullResultsLoading && (fullResultsError || summaryPartialWarning) && (
          <Card className="border-yellow-200 bg-yellow-50/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <div className="font-medium text-sm sm:text-base">Partial report loaded</div>
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    {summaryPartialWarning
                      ? `Summary sections are showing the first ${SUMMARY_ITEMS_PER_PAGE.toLocaleString()} results ` +
                        `out of ${fullResultsData?.total_count.toLocaleString() || "many"}.`
                      : "Some summary sections may be incomplete because full results could not be loaded."}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
